// Copilot panel open/close/unload telemetry (Ag11 diagnosis).
//
// QA reported the panel "closing itself" twice in one long conversation on
// next.oppizi.com. The library reports every close through
// `onOpenChange(false, reason)` (reason: 'close-button' | 'imperative' |
// 'session-invalid'), and the only other way the panel can vanish is a full
// page load. Neither is visible in analytics today, so nothing can name the
// cause after the fact. This module captures all of them into PostHog with
// enough context to tell them apart next time.
//
// Deliberately dependency-free (no React, no TypeScript import, no app config)
// so the SAME file is exercised by a plain `node:test` harness
// (scripts/test-copilot-panel-telemetry.mjs) — jest is broken repo-wide. The
// event names come from `campaign-builder/analytics/cbAnalytics.ts` and are
// injected by the host (`ClientChat.jsx`).
//
// Privacy: props are built field by field from ids, reasons, paths and
// booleans only. Never add message text, emails or names here.

const defaultNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
const defaultPath = () => (typeof window !== "undefined" ? window.location.pathname : "");
const defaultVisibility = () => (typeof document !== "undefined" ? document.visibilityState : undefined);

// posthog-js capture options that make the event leave the page even though
// the page is going away: skip the batch queue and use `sendBeacon`, which
// the browser delivers after unload.
export const UNLOAD_CAPTURE_OPTIONS = Object.freeze({ send_instantly: true, transport: "sendBeacon" });

/**
 * Same-origin check for an outgoing navigation the host knows about (e.g. an
 * agent `navigate` event). Returns null when the URL cannot be parsed.
 * @param {string} url
 * @param {string} [origin]
 * @returns {boolean | null}
 */
export const isSameOrigin = (url, origin = typeof window !== "undefined" ? window.location.origin : "") => {
  try {
    return new URL(url, origin || undefined).origin === origin;
  } catch {
    return null;
  }
};

/**
 * Build the host-side handlers for the library's `onOpenChange` prop and the
 * window `pagehide` event.
 *
 * @param {{
 *   posthog: { capture?: (name: string, props?: object, options?: object) => void } | null | undefined,
 *   events: { OPENED: string, CLOSED: string, UNLOADED_WHILE_OPEN: string },
 *   host: "builder" | "next",
 *   getConversationId?: () => string | null | undefined,
 *   getPath?: () => string,
 *   getVisibility?: () => string | undefined,
 *   now?: () => number,
 *   log?: (message: string, detail: object) => void,
 * }} deps
 */
export const createPanelTelemetry = ({
  posthog,
  events,
  host,
  getConversationId,
  getPath = defaultPath,
  getVisibility = defaultVisibility,
  now = defaultNow,
  log = (message, detail) => console.info(message, detail),
}) => {
  let isOpen = false;
  let openedAt = null;
  // The destination of a navigation the host itself started (agent
  // `navigate`); unknown (null) for everything else — a reload, a link, a
  // `window.location.replace` the host does not route through here.
  let nextUrlSameOrigin = null;

  const capture = (name, props, options) => {
    try {
      if (options) posthog?.capture?.(name, props, options);
      else posthog?.capture?.(name, props);
    } catch (error) {
      // Analytics must never break the panel.
      console.error("copilot panel telemetry: capture failed:", error);
    }
  };

  const onOpenChange = (open, reason) => {
    const path = getPath();
    if (open) {
      isOpen = true;
      openedAt = now();
      capture(events.OPENED, { reason, host, path });
      return;
    }
    const wasOpen = isOpen;
    isOpen = false;
    const openForMs = wasOpen && openedAt != null ? Math.max(0, Math.round(now() - openedAt)) : undefined;
    openedAt = null;
    const conversationId = getConversationId?.() || undefined;
    const props = { reason, host, path, visibility: getVisibility() };
    if (conversationId) props.conversation_id = conversationId;
    if (openForMs !== undefined) props.open_for_ms = openForMs;
    capture(events.CLOSED, props);
    // Visible in a tester's screen recording / console export — same
    // convention as platform-next's `[Copilot debug]` line.
    log("[Copilot debug] panel closed", { reason, path });
  };

  /**
   * @param {{ persisted?: boolean } | undefined} event the `pagehide` event
   * @param {() => boolean} [isPanelOpen] the library's imperative `isOpen()`;
   *   falls back to the state tracked through `onOpenChange`.
   */
  const onPageHide = (event, isPanelOpen) => {
    const open = typeof isPanelOpen === "function" ? isPanelOpen() : isOpen;
    if (!open) return;
    capture(
      events.UNLOADED_WHILE_OPEN,
      {
        host,
        path: getPath(),
        next_url_same_origin: nextUrlSameOrigin,
        persisted: !!(event && event.persisted),
      },
      UNLOAD_CAPTURE_OPTIONS
    );
  };

  /** Record where a host-initiated navigation is going, ahead of `pagehide`. */
  const noteOutgoingNavigation = (url) => {
    nextUrlSameOrigin = isSameOrigin(url);
  };

  return { onOpenChange, onPageHide, noteOutgoingNavigation, isOpen: () => isOpen };
};
